import { useMemo, useState } from 'react';
import { Button, Header, Icon, Message, Segment, Table } from 'semantic-ui-react';
import { AM, FINANCE, LEVEL_NAME, canUnapprove, describe, standingApprovals } from '../approvals.js';

/* Admin: every approved report period, with the Finance and Account Manager approvals and a button to take either one away.
 * Finance can remove either; an Account Manager can remove the AM approval on their own reports. A reason is required and is kept in the audit log.
 * Removing Finance approval also removes the AM approval that depended on it. */
export default function AdminTab({ reports, approvals, user, busy, onUnapprove }) {
  const [reportId, setReportId] = useState('');
  const [year, setYear] = useState('');
  const [pick, setPick] = useState(null);                // { group, level } waiting for a reason
  const [reason, setReason] = useState('');

  const clientOf = useMemo(() => new Map(reports.map((r) => [r.settlement_report_id, r.client_name])), [reports]);
  const groups = useMemo(() => standingApprovals(approvals).sort((a, b) => a.id.localeCompare(b.id) || b.start.localeCompare(a.start)), [approvals]);   // approvals that still stand
  const ids = useMemo(() => [...new Set(groups.map((g) => g.id))], [groups]);
  const years = useMemo(() => [...new Set(groups.map((g) => g.start.slice(0, 4)))].sort().reverse(), [groups]);
  const shown = groups.filter((g) => (!reportId || g.id === reportId) && (!year || g.start.slice(0, 4) === year));

  const reportOf = (g) => reports.find((r) => r.settlement_report_id === g.id && r.report_year === g.year && r.as_at_date === g.asAt)
    || reports.find((r) => r.settlement_report_id === g.id) || { settlement_report_id: g.id, report_year: g.year, as_at_date: g.asAt };
  const statusOf = (g) => ({ finance: g.finance, account_manager: g.account_manager });
  const cascade = pick && pick.level === FINANCE && pick.group.account_manager;

  async function confirm(e) {
    e.preventDefault();
    if (!reason.trim() || !pick) return;
    const ok = await onUnapprove({ level: pick.level, report: reportOf(pick.group), period: { type: pick.group.type, label: pick.group.label, start: pick.group.start, title: pick.group.title }, reason });
    if (ok) { setPick(null); setReason(''); }
  }
  const button = (g, level) => {
    const row = g[level];
    if (!row) return <span className="hint">Not approved</span>;
    const check = canUnapprove(user, level, reportOf(g), statusOf(g));
    return (
      <div>
        <div className="approvalcell">{describe(row)}</div>
        <Button type="button" basic negative compact size="mini" content="Unapprove" disabled={!check.ok || !!busy} title={check.ok ? undefined : check.reason}
          aria-label={`Unapprove ${LEVEL_NAME[level]} ${g.id} ${g.title}`} onClick={() => { setPick({ group: g, level }); setReason(''); }} />
      </div>
    );
  };

  if (!user || ![FINANCE, AM].includes(user.role)) {
    return <Message warning role="note">Only Finance and Account Managers can use Admin.</Message>;
  }
  return (
    <section aria-labelledby="admin-heading">
      <Header as="h2" id="admin-heading" className="titlebar">
        <Icon name="setting" />
        <Header.Content>Admin<Header.Subheader>Take an approval away so a report can be corrected and approved again</Header.Subheader></Header.Content>
      </Header>
      <p className="hint">
        {user.role === FINANCE
          ? 'As Finance you can remove Finance or Account Manager approval. Removing Finance approval also removes the Account Manager approval that followed it.'
          : 'As an Account Manager you can remove the Account Manager approval on your own reports.'} Every removal is added to the report’s Audit tab with who did it, when and why.
      </p>
      <div className="toolbar" role="group" aria-label="Filter the approvals">
        <label htmlFor="admin-report">Report</label>
        <select id="admin-report" className="ui dropdown" value={reportId} onChange={(e) => setReportId(e.target.value)}>
          <option value="">All reports</option>
          {ids.map((id) => <option key={id} value={id}>{id} – {clientOf.get(id) || 'No client name'}</option>)}
        </select>
        <label htmlFor="admin-year">Year</label>
        <select id="admin-year" className="ui dropdown" value={year} onChange={(e) => setYear(e.target.value)}>
          <option value="">All years</option>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {pick && (
        <Segment color="red" as="form" onSubmit={confirm} aria-label="Unapprove" className="unapproveform">
          <Header as="h3" size="small">Unapprove {LEVEL_NAME[pick.level]} – {pick.group.id} {pick.group.title}</Header>
          {cascade && <Message warning size="small" role="note">The Account Manager approval for this period will be removed as well, because it followed this one.</Message>}
          <label htmlFor="admin-reason">Reason (required)</label>
          <textarea id="admin-reason" rows={2} className="reason" value={reason} onChange={(e) => setReason(e.target.value)} />
          <div className="toolbar">
            <Button type="submit" negative content={`Unapprove ${LEVEL_NAME[pick.level]}`} disabled={!reason.trim() || !!busy} loading={busy === 'unapprove'} />
            <Button type="button" basic content="Cancel" onClick={() => { setPick(null); setReason(''); }} />
          </div>
        </Segment>
      )}

      {!groups.length ? <Segment placeholder><Header icon><Icon name="check circle outline" />Nothing is approved at the moment.</Header></Segment>
        : !shown.length ? <Message info>No approvals match.</Message> : (
          <div className="gridwrap" tabIndex={0} role="region" aria-label="Approved reports (scrollable)">
            <Table celled compact unstackable className="datagrid hometable">
              <caption className="visually-hidden">Approved report periods and their Finance and Account Manager approvals</caption>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell scope="col" className="lab">Settlement Report ID</Table.HeaderCell>
                  <Table.HeaderCell scope="col">Client Name</Table.HeaderCell>
                  <Table.HeaderCell scope="col">Period</Table.HeaderCell>
                  <Table.HeaderCell scope="col">Finance approval</Table.HeaderCell>
                  <Table.HeaderCell scope="col">Account Manager approval</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {shown.map((g) => (
                  <Table.Row key={g.key}>
                    <Table.HeaderCell scope="row" className="lab">{g.id}</Table.HeaderCell>
                    <Table.Cell>{clientOf.get(g.id) || '–'}</Table.Cell>
                    <Table.Cell>{g.title}</Table.Cell>
                    <Table.Cell>{button(g, FINANCE)}</Table.Cell>
                    <Table.Cell>{button(g, AM)}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
    </section>
  );
}
